import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { ICustomer } from "../../core/interfaces/customer.interface";

import { CustomerDecisionTypeEnum } from "../../core/enums/customer-decision-type.enum";
import { OverflowErrorsEnum } from "../../core/enums/overflow-errors.enum";

import { ApplicationConstantsEnum } from "src/app/core/enums/applications-constants.enum";
import { ClientEducationalService } from "./client-educational.service";
import { CustomerService } from "./customer.service";
import { DocumentTypeTapEnum } from "src/app/core/enums/document-type-tap.enum";
import { FileManagerService } from "./file-manager.service";
import { OverflowErrorsService } from "./overflow-errors.service";
import { ValidateResponseService } from "./validate-response.service";

import { PayrollRemoteService } from "../remote/payroll-remote.service";

@Injectable({
  providedIn: "root",
})
export class PayrollService {
  customer: ICustomer;

  constructor(
    private clientEducationalService: ClientEducationalService,
    private customerService: CustomerService,
    private fileManagerService: FileManagerService,
    private overflowErrorsService: OverflowErrorsService,
    private payrollServiceRemote: PayrollRemoteService,
    private validateResponseService: ValidateResponseService,
  ) {}

  public validateClient(dataCustomer: any): Observable<boolean> {
    this.customerService.setCustomer({
      documentType: dataCustomer.documentType,
      documentId: dataCustomer.documentNumber,
    });

    return this.payrollServiceRemote
      .isClientEnabledForUploadFiles(dataCustomer)
      .pipe(
        map((dataClientEnabledForUploadFiles: any) => {
          if (
            !this.validateResponseService.validateClientEnabledForUploadFiles(
              dataClientEnabledForUploadFiles,
            )
          ) {
            throw {};
          }

          this.setDataCustomer(dataClientEnabledForUploadFiles);

          return dataClientEnabledForUploadFiles;
        }),
        catchError((errorDataAuthentication: any) => {
          this.overflowErrorsService.setOverflowError(
            errorDataAuthentication.error.code
              ? errorDataAuthentication.error.code
              : OverflowErrorsEnum.BucketFile002,
          );

          return throwError([]);
        }),
      );
  }

  sendFileTap(
    file: File,
    payrollFileType: DocumentTypeTapEnum,
    urlFile: string,
  ) {
    if (
      this.clientEducationalService.getToggleEducational() ||
      this.clientEducationalService.getToggleOptimization()
    ) {
      const codeName =
        payrollFileType === DocumentTypeTapEnum.LABOR_CERTIFICATION
          ? ApplicationConstantsEnum.LABOR_CERTIFICATION_OCR_CODE
          : ApplicationConstantsEnum.PAYROLL_CHECKS_OCR_CODE;

      const body = this.buildRequestBodySendTap(
        codeName,
        payrollFileType,
        file,
        urlFile,
      );

      return this.payrollServiceRemote.sendFileToTap(body).pipe(
        map(() => {
          return true;
        }),
        catchError(() => {
          return throwError([]);
        }),
      );
    } else {
      return this.setPayrollFile(file);
    }
  }

  setPayrollFile(file: File): Observable<boolean> {
    const fileExtension = this.fileManagerService.getFileExtension(file);
    let customer = this.customerService.getCustomer();

    return this.payrollServiceRemote
      .setPayrollFile({
        documentType: customer.documentType,
        data: JSON.parse(this.clientEducationalService.getDoublePension()),
        documentNumber: customer.documentId,
        obligationId: customer.obligationId,
        fileName: `${customer.obligationId}_${customer.documentType}_${customer.documentId}.${fileExtension}`,
      })
      .pipe(
        map(() => {
          return true;
        }),
        catchError(() => {
          return throwError([]);
        }),
      );
  }

  cleanSession() {
    this.customerService.setCustomer(undefined);
  }

  private async setDataCustomer(dataClientEnabledForUploadFiles: any) {
    let customer = this.customerService.getCustomer();

    customer.decisionType = dataClientEnabledForUploadFiles.newClient
      ? CustomerDecisionTypeEnum.NEW_LOAN
      : CustomerDecisionTypeEnum.EXTEND_LOAN;

    customer.salesJourneyId = dataClientEnabledForUploadFiles.salesJourneyId;

    this.customerService.setCustomer(customer);

    this.clientEducationalService.setDoublePension(
      dataClientEnabledForUploadFiles.data,
    );

    this.clientEducationalService.setSector(
      parseInt(dataClientEnabledForUploadFiles.sectorCode),
    );

    this.clientEducationalService.setPayerName(
      dataClientEnabledForUploadFiles.payerName,
    );
    this.clientEducationalService.setPayerNit(
      dataClientEnabledForUploadFiles.payerNit,
    );

    await new Promise((resolve) => {
      if (dataClientEnabledForUploadFiles.documentPreload) {
        this.clientEducationalService.setListFileDocuments(
          dataClientEnabledForUploadFiles.documentPreload.documents,
        );
      }
      resolve(true);
    });
  }

  private buildRequestBodySendTap(
    codeName: string,
    documentTypeTap: DocumentTypeTapEnum,
    file: File,
    urlFile: string,
  ): any {
    this.customer = this.customerService.getCustomer();
    return {
      journeyId: this.customer.salesJourneyId,
      data: this.clientEducationalService.getDoublePension(),
      documentType: documentTypeTap,
      documentName: codeName,
      fileData: {
        fileBucket: this.getFileBucket(urlFile),
        fileExtension: this.fileManagerService.getFileExtension(file),
        fileUrl: this.getFileUrl(urlFile),
      },
      loanData: {
        clientDocumentNumber: this.customer.documentId.toString(),
        clientDocumentType: this.customer.documentType,
        payrollPayer: this.clientEducationalService.getPayerName(),
        payerNit: this.clientEducationalService.getPayerNit(),
        sectorNumber: this.clientEducationalService.getSector().toString(),
      },
    };
  }

  private getFileBucket(urlComplete: string): string {
    const bucket = urlComplete.split("/")[2];
    return bucket.split(".")[0];
  }

  private getFileUrl(urlComplete: string): string {
    const endpoint = urlComplete.split("/")[3];
    const fileName = urlComplete.split("/")[4];
    return `/${endpoint}/${fileName.split("?")[0]}`;
  }
}
